import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Headphones,
  Settings,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Pause,
  Square,
  Volume2,
  Sun,
  Moon,
  Type,
  X,
  Download,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { booksService } from '../../services/api';
import { readingSessionsService } from '../../services/api/reading-sessions.service';
import { LoadingSpinner } from '../../components/common';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Theme = 'light' | 'sepia' | 'dark';

interface BookDetails {
  title: string;
  author: string;
  downloadable: boolean;
  fileUrl: string;
}

export const BookReaderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Touch gesture state
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  // PDF state
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [pageTexts, setPageTexts] = useState<Map<number, string>>(new Map());

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [bookDetails, setBookDetails] = useState<BookDetails | null>(null);

  // Reading settings
  const [theme, setTheme] = useState<Theme>('light');
  const [fontSize, setFontSize] = useState(18);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [isScannedPdf, setIsScannedPdf] = useState(false);

  // Text-to-speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const speechSynthesis = window.speechSynthesis;
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const activeSecondsRef = useRef(0);
  const sessionStartRef = useRef<Date | null>(null);
  const sessionActiveRef = useRef(true);
  const sessionIntervalRef = useRef<number | null>(null);

  // Reading progress
  const [readingProgress, setReadingProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('');

  // Page input
  const [showPageInput, setShowPageInput] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');

  // Theme configurations
  const themeConfig = {
    light: { bg: 'bg-white', text: 'text-slate-900', secondary: 'text-slate-500' },
    sepia: { bg: 'bg-amber-50', text: 'text-amber-900', secondary: 'text-amber-700' },
    dark: { bg: 'bg-slate-900', text: 'text-slate-100', secondary: 'text-slate-400' },
  };

  // Update current time
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const recordReadingSession = useCallback(async (durationSeconds: number, startAt: Date, endAt: Date) => {
    if (!id || durationSeconds <= 0) return;

    try {
      await readingSessionsService.createReadingSession({
        bookId: id,
        durationSeconds,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
      });
    } catch (err) {
      console.warn('Unable to record reading session', err);
    }
  }, [id]);

  const flushReadingSession = useCallback(() => {
    if (!sessionStartRef.current) return;
    const durationSeconds = activeSecondsRef.current;
    if (durationSeconds <= 0) return;

    const startAt = sessionStartRef.current;
    const endAt = new Date();
    activeSecondsRef.current = 0;
    sessionStartRef.current = endAt;

    void recordReadingSession(durationSeconds, startAt, endAt);
  }, [recordReadingSession]);

  const handleVisibilityChange = useCallback(() => {
    sessionActiveRef.current = document.visibilityState === 'visible';
  }, []);

  const handleBeforeUnload = useCallback(() => {
    flushReadingSession();
  }, [flushReadingSession]);

  // Load available voices for TTS
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      const englishVoices = voices.filter(voice => voice.lang.startsWith('en'));
      setAvailableVoices(englishVoices.length > 0 ? englishVoices : voices);
      if (englishVoices.length > 0 && !selectedVoice) {
        setSelectedVoice(englishVoices[0].name);
      }
    };

    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      speechSynthesis.cancel();
    };
  }, [selectedVoice, speechSynthesis]);

  // Calculate container dimensions for responsive PDF
  const updateContainerDimensions = useCallback(() => {
    if (pdfContainerRef.current) {
      const width = pdfContainerRef.current.clientWidth; // Get width of PDF container
      setContainerWidth(width);
    }
  }, []);

  useEffect(() => {
    updateContainerDimensions();
    window.addEventListener('resize', updateContainerDimensions);
    return () => window.removeEventListener('resize', updateContainerDimensions);
  }, [updateContainerDimensions]);

  // Track active reading time while the PDF reader is visible
  useEffect(() => {
    sessionStartRef.current = new Date();
    sessionActiveRef.current = document.visibilityState === 'visible';
    if (sessionIntervalRef.current) {
      window.clearInterval(sessionIntervalRef.current);
    }
    sessionIntervalRef.current = window.setInterval(() => {
      if (sessionActiveRef.current) {
        activeSecondsRef.current += 1;
      }
    }, 1000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (sessionIntervalRef.current) {
        window.clearInterval(sessionIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushReadingSession();
    };
  }, [id, flushReadingSession, handleBeforeUnload, handleVisibilityChange]);

  // Update dimensions when scale changes
  useEffect(() => {
    updateContainerDimensions();
  }, [scale, updateContainerDimensions]);

  // Helper function to extract text from PDF page
  const extractTextFromPage = useCallback(async (pdf: any, pageNum: number): Promise<string> => {
    try {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const text = textContent.items
        .map((item: any) => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text;
    } catch {
      return '';
    }
  }, []);

  // Fetch book details and read URL, then load PDF metadata
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const [readUrlData, bookData] = await Promise.all([
          booksService.getReadUrl(id),
          booksService.getBookById(id)
        ]);
        setFileUrl(readUrlData.url);
        setBookDetails({
          title: bookData.title,
          author: bookData.author,
          downloadable: bookData.downloadable ?? true,
          fileUrl: bookData.fileUrl
        });

        // Load PDF metadata and first page text
        const loadingTask = pdfjs.getDocument(readUrlData.url);
        const pdf = await loadingTask.promise;
        setNumPages(pdf.numPages);

        // Extract text from first page
        const page = await pdf.getPage(1);
        const textContent = await page.getTextContent();
        const text = textContent.items
          .map((item: any) => item.str)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        setPageTexts(prev => new Map(prev).set(1, text));
        setIsScannedPdf(!text);

      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load book');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const onDocumentLoadSuccess = async ({ numPages: totalPages }: { numPages: number }) => {
    setNumPages(totalPages);
    setIsLoading(false);

    // Load text for current page
    if (fileUrl) {
      const loadingTask = pdfjs.getDocument(fileUrl);
      const pdf = await loadingTask.promise;
      const text = await extractTextFromPage(pdf, pageNumber);
      setPageTexts(prev => new Map(prev).set(pageNumber, text));
    }
  };

  // Load text when page changes
  useEffect(() => {
    const loadPageText = async () => {
      if (!fileUrl || pageTexts.has(pageNumber)) return;

      try {
        const loadingTask = pdfjs.getDocument(fileUrl);
        const pdf = await loadingTask.promise;
        const text = await extractTextFromPage(pdf, pageNumber);
        setPageTexts(prev => new Map(prev).set(pageNumber, text));
        setIsScannedPdf(!text);
      } catch (err) {
        console.error('Failed to extract text:', err);
      }
    };

    loadPageText();
  }, [fileUrl, pageNumber, extractTextFromPage, pageTexts]);

  // Update reading progress and sync page input
  useEffect(() => {
    if (numPages > 0) {
      setReadingProgress(Math.round((pageNumber / numPages) * 100));
    }
    setPageInput(String(pageNumber));
  }, [pageNumber, numPages]);

  // Navigation
  const goToPrevPage = () => {
    const newPage = Math.max(pageNumber - 1, 1);
    setPageNumber(newPage);
    setPageInput(String(newPage));
    stopSpeaking();
  };

  const goToNextPage = () => {
    const newPage = Math.min(pageNumber + 1, numPages);
    setPageNumber(newPage);
    setPageInput(String(newPage));
    stopSpeaking();
  };


  // Text-to-Speech functions
  const getCurrentPageText = () => {
    return pageTexts.get(pageNumber) || 'No text available for this page.';
  };

  const startSpeaking = () => {
    if (isSpeaking && !isPaused) {
      speechSynthesis.pause();
      setIsPaused(true);
      return;
    }

    if (isPaused) {
      speechSynthesis.resume();
      setIsPaused(false);
      return;
    }

    const text = getCurrentPageText();
    if (!text) return;

    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speechRate;

    const voice = availableVoices.find(v => v.name === selectedVoice);
    if (voice) utterance.voice = voice;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      // Auto-advance to next page
      if (pageNumber < numPages) {
        goToNextPage();
        setTimeout(() => startSpeaking(), 500);
      }
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utteranceRef.current = utterance;
    speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    // TODO: Save bookmark to backend
  };

  const handleDownload = () => {
    if (!bookDetails?.downloadable) {
      alert('This book is not available for download.');
      return;
    }

    if (bookDetails.fileUrl) {
      const link = document.createElement('a');
      link.href = bookDetails.fileUrl;
      link.download = `${bookDetails.title}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Download URL not available.');
    }
  };

  // Zoom functions
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 3.0)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 0.5)); // Min 0.5x zoom
  };

  const handleZoomReset = () => {
    setScale(1.0);
  };

  // Touch gesture handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    // Only trigger swipe if horizontal movement is greater than vertical (to avoid conflict with scroll)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe left - next page
        goToNextPage();
      } else {
        // Swipe right - previous page
        goToPrevPage();
      }
    }
  };

  // Page input handler
  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNum = parseInt(pageInputValue);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= numPages) {
      setPageNumber(pageNum);
      setShowPageInput(false);
      setPageInputValue('');
      stopSpeaking();
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center p-4">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentTheme = themeConfig[theme];

  // Calculate PDF dimensions with zoom
  // containerWidth is measured from pdfContainerRef which already accounts for padding
  const baseWidth = containerWidth > 0 ? containerWidth - 40 : undefined; // Subtract 40px for PDF container internal spacing
  const scaledWidth = baseWidth ? baseWidth * scale : undefined;

  return (
    <div className={`fixed inset-0 flex flex-col overflow-hidden ${currentTheme.bg} transition-colors duration-300`}>
      {/* Header - compact design */}
      <header
        className={`flex-shrink-0 flex items-center justify-between px-3 sm:px-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'} border-b ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'}`}
        style={{
          paddingTop: 'max(0.375rem, env(safe-area-inset-top))',
          paddingBottom: '0.375rem'
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="p-1 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors"
        >
          <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        </button>

        {/* Title and page info combined on desktop */}
        <div className="flex-1 text-center mx-4">
          <h1 className={`text-xs sm:text-sm font-semibold ${currentTheme.text} truncate`}>
            {bookDetails?.title || 'Loading...'}
          </h1>
          <button
            onClick={() => {
              setShowPageInput(true);
              setPageInputValue(String(pageNumber));
            }}
            className={`text-[9px] sm:text-[10px] ${currentTheme.secondary} hover:text-primary-600 transition-colors`}
          >
            Page {pageNumber} of {numPages}
          </button>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <span className="text-[10px] sm:text-xs font-medium mr-1 sm:mr-2 hidden sm:inline">{currentTime}</span>

          {/* Zoom controls */}
          <div className="flex items-center gap-0.5 sm:gap-1 mr-1 sm:mr-2">
            <button
              onClick={handleZoomOut}
              className="p-3 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors"
              title="Zoom out"
              disabled={scale <= 0.5}
            >
              <ZoomOut size={18} className="sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={handleZoomReset}
              className="p-3 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors"
              title="Reset zoom"
            >
              <RotateCcw size={16} className="sm:w-4 sm:h-4" />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-3 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors"
              title="Zoom in"
              disabled={scale >= 3.0}
            >
              <ZoomIn size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>

          <button
            onClick={handleDownload}
            className={`p-2 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors ${!bookDetails?.downloadable ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={bookDetails?.downloadable ? "Download book" : "Download not available"}
            disabled={!bookDetails?.downloadable}
          >
            <Download size={20} className="sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={toggleBookmark}
            className={`p-2 sm:p-1.5 hover:bg-black/10 rounded-full transition-colors ${isBookmarked ? 'text-primary-600' : ''}`}
          >
            <Bookmark size={20} className="sm:w-5 sm:h-5" fill={isBookmarked ? 'currentColor' : 'none'} />
          </button>
        </div>
      </header>

      {/* Main content area - fills remaining space */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {fileUrl && (
          <div
            ref={pdfContainerRef}
            className={`w-full h-full overflow-auto bg-slate-100 ${scale > 1.5 ? 'p-1' : 'p-2 sm:p-3 md:p-4'}`}
            style={{
              display: 'flex',
              justifyContent: scale <= 1.0 ? 'center' : 'flex-start',
              alignItems: scale <= 1.0 ? 'center' : 'flex-start',
            }}
          >
            <div className="sr-only">
              {isScannedPdf ? 'This PDF appears to be scanned and may not have selectable text.' : ''}
            </div>
            <div style={{ margin: scale <= 1.0 ? 'auto' : '0' }}>
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<LoadingSpinner size="lg" />}
                error={<div className="text-red-600 text-center py-8">Failed to load PDF</div>}
              >
                <Page
                  pageNumber={pageNumber}
                  width={scaledWidth}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-lg"
                />
              </Document>
            </div>
          </div>
        )}
      </div>

      {/* Bottom toolbar - compact on mobile with safe area */}
      <div className={`flex-shrink-0 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} border-t ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'} px-3 sm:px-4 pt-2 sm:pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]`}>
        {/* Progress bar */}
        <div className="w-full h-0.5 sm:h-1 bg-slate-200 rounded-full mb-2 sm:mb-3 overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${readingProgress}%` }}
          />
        </div>

        <div className="flex items-center justify-between gap-1 sm:gap-2">
          {/* Page navigation - compact on mobile */}
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className={`p-1.5 sm:p-2 rounded-full disabled:opacity-30 ${theme === 'dark' ? 'text-white hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
            </button>
            <form onSubmit={(e) => { e.preventDefault(); const targetPage = parseInt(pageInput, 10); if (!isNaN(targetPage) && targetPage >= 1 && targetPage <= numPages) { setPageNumber(targetPage); } else { setPageInput(String(pageNumber)); } }} className="flex items-center gap-0.5 sm:gap-1">
              <input
                type="number"
                min="1"
                max={numPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={() => { const targetPage = parseInt(pageInput, 10); if (!isNaN(targetPage) && targetPage >= 1 && targetPage <= numPages) { setPageNumber(targetPage); } else { setPageInput(String(pageNumber)); } }}
                className={`w-10 sm:w-12 px-1 py-0.5 sm:py-1 text-xs sm:text-sm font-medium text-center border rounded ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-slate-300 text-slate-900'
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
              <span className={`text-xs sm:text-sm font-medium ${currentTheme.text}`}>
                / {numPages}
              </span>
            </form>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className={`p-1.5 sm:p-2 rounded-full disabled:opacity-30 ${theme === 'dark' ? 'text-white hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              <ChevronRight size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Voice reader controls */}
          <div className={`flex items-center ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100'} rounded-full p-0.5 sm:p-1`}>
            <button
              onClick={() => {
                if (isSpeaking) {
                  stopSpeaking();
                } else {
                  startSpeaking();
                }
              }}
              className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                isSpeaking
                  ? 'bg-primary-600 text-white'
                  : theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              {isSpeaking && !isPaused ? <Pause size={14} className="sm:w-4 sm:h-4" /> : <Headphones size={14} className="sm:w-4 sm:h-4" />}
              <span className="hidden xs:inline">Listen</span>
            </button>
          </div>

          {/* Settings & Actions */}
          <div className="flex items-center">
            {isSpeaking && (
              <button
                onClick={stopSpeaking}
                className={`p-1.5 sm:p-2 rounded-full ${theme === 'dark' ? 'text-red-400 hover:bg-slate-700' : 'text-red-600 hover:bg-slate-100'}`}
              >
                <Square size={16} className="sm:w-5 sm:h-5" />
              </button>
            )}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-1.5 sm:p-2 rounded-full ${theme === 'dark' ? 'text-white hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              <Settings size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className={`${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} w-full sm:w-96 sm:rounded-2xl rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${currentTheme.text}`}>Reading Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
              >
                <X size={20} className={currentTheme.text} />
              </button>
            </div>

            {/* Theme selection */}
            <div className="mb-6">
              <label className={`text-sm font-medium ${currentTheme.secondary} block mb-3`}>Theme</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setTheme('light')}
                  className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                    theme === 'light' ? 'border-primary-600 bg-primary-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  <Sun size={20} className="mx-auto text-amber-500" />
                  <span className="text-xs text-slate-600 mt-1 block">Light</span>
                </button>
                <button
                  onClick={() => setTheme('sepia')}
                  className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                    theme === 'sepia' ? 'border-primary-600 bg-amber-100' : 'border-slate-200 bg-amber-50'
                  }`}
                >
                  <BookOpen size={20} className="mx-auto text-amber-700" />
                  <span className="text-xs text-amber-800 mt-1 block">Sepia</span>
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                    theme === 'dark' ? 'border-primary-600 bg-slate-700' : 'border-slate-600 bg-slate-800'
                  }`}
                >
                  <Moon size={20} className="mx-auto text-slate-300" />
                  <span className="text-xs text-slate-300 mt-1 block">Dark</span>
                </button>
              </div>
            </div>

            {/* Font size */}
            <div className="mb-6">
              <label className={`text-sm font-medium ${currentTheme.secondary} block mb-3`}>
                <Type size={16} className="inline mr-2" />
                Font Size: {fontSize}px
              </label>
              <input
                type="range"
                min="14"
                max="28"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Small</span>
                <span>Large</span>
              </div>
            </div>

            {/* Text-to-Speech settings */}
            <div className="mb-6">
              <label className={`text-sm font-medium ${currentTheme.secondary} block mb-3`}>
                <Volume2 size={16} className="inline mr-2" />
                Speech Rate: {speechRate}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={speechRate}
                onChange={(e) => setSpeechRate(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Slow</span>
                <span>Fast</span>
              </div>
            </div>

            {/* Voice selection */}
            {availableVoices.length > 0 && (
              <div className="mb-6">
                <label className={`text-sm font-medium ${currentTheme.secondary} block mb-3`}>
                  Voice
                </label>
                <select
                  value={selectedVoice}
                  onChange={(e) => setSelectedVoice(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                >
                  {availableVoices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))}
                </select>
              </div>
            )}

          </div>
        </div>
      )}
      {showPageInput && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} rounded-xl p-6 w-full max-w-sm`}>
            <h3 className={`text-lg font-semibold ${currentTheme.text} mb-4`}>Go to Page</h3>
            <form onSubmit={handlePageInputSubmit}>
              <input
                type="number"
                min="1"
                max={numPages}
                value={pageInputValue}
                onChange={(e) => setPageInputValue(e.target.value)}
                placeholder={`Enter page (1-${numPages})`}
                autoFocus
                className={`w-full px-4 py-3 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-slate-300 text-slate-900'
                } focus:outline-none focus:ring-2 focus:ring-primary-600`}
              />
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPageInput(false);
                    setPageInputValue('');
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    theme === 'dark'
                      ? 'bg-slate-700 text-white hover:bg-slate-600'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                >
                  Go
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
